import {Combo, PopupAnchor, SourceAnchor} from '@mintdata-wsp/common/components/combo/Combo';
import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {Credentials} from '@mintdata-wsp/common/state/DataState';
import {OAuth2ProviderName} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/OAuth2';
import {DataLineColorType} from '@mintdata-wsp/wsp/ui/components/dataline/DataLine';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {ErrorPopupSide, ErrorStyle, Input, InputSize, InputStyle} from '@mintdata-wsp/wsp/ui/components/forms/Input';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {ButtonSize, ButtonStyle, WSPButton} from '@mintdata-wsp/wsp/ui/components/general/WSPButton';
import {IListItemState, ListModel} from '@mintdata/mint-kit/api/models/List';
import {PopupSize} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IStyles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./EditCredentials.less');
const cx = classnames.bind(styles);

export interface CreateCredentialsProps
{
    credentials?:Credentials;
    onClose:() => void;
    onChange:(credentials:Credentials) => void;
    className?:string;
    buttonTitle:string;
    isReadOnly?:boolean;
}

@observer
export class EditCredentials extends React.Component<CreateCredentialsProps, any>
{
    @observable
    private _credentials:Credentials;

    private _dropdownAuthProviderItems;

    componentWillMount():void
    {
        const {credentials} = this.props;

        this._initCredentials(credentials);
        this._initAuthProvidersItems(credentials);
    }

    render()
    {
        const {className, buttonTitle} = this.props;

        const credentials:Credentials = this._credentials;

        const isDisabled = !this._isValidInputs();

        const colorType = DataLineColorType.Dark;
        const colorTypeStr = 'colorType-' + DataLineColorType[colorType];

        return (
            <Row className={cx('createCredentials', className, colorTypeStr)}>

                <Column className={cx('fields')}>

                    <Row className={cx('firstLine')}>
                        <Input
                            className={cx('inputNameWrapper')}
                            inputClassName={cx('inputName')}
                            style={InputStyle.Simple}
                            size={InputSize.Small}
                            placeholder={'Credentials name'}
                            initialValue={credentials.name}
                            isInitiallyFocused={true}
                            isInitiallySelected={true}
                            onChange={this._onChangeName}

                            isValidFn={(name:string) =>
                            {
                                return this._isValidName(name, credentials.id);
                            }}
                            getErrorMessageFn={() => 'Credentials name should be unique'}
                            errorStyle={ErrorStyle.BdpForms}
                            errorPopupSide={ErrorPopupSide.Bottom}
                        />

                        <Combo
                            className={cx('dropdownPosition')}
                            dataSource={this._dropdownAuthProviderItems}
                            onSelectItem={this._onSelectAuthProvider}
                            contentRenderer={this._authProviderCaptionRenderer}
                            itemRenderer={this._positionItemsRenderer}
                            sourceAnchor={SourceAnchor.RightBottom}
                            popoverAnchor={PopupAnchor.RightTop}
                            popupSize={PopupSize.Original}
                            theme={{content: cx('dropdownPosition-items')}}
                        />

                    </Row>

                    <Input
                        className={cx('inputTextWrapper')}
                        inputClassName={cx('inputText')}
                        style={InputStyle.Simple}
                        size={InputSize.Small}
                        placeholder={'Client Id'}
                        initialValue={credentials.clientId}
                        onChange={this._onChangeClientId}
                    />

                    <Input
                        isPassword={true}
                        className={cx('inputTextWrapper')}
                        inputClassName={cx('inputText')}
                        style={InputStyle.Simple}
                        size={InputSize.Small}
                        placeholder={'Client Secret'}
                        initialValue={credentials.clientSecret}
                        onChange={this._onChangeClientSecret}
                    />

                    <Input
                        className={cx('inputTextWrapper')}
                        inputClassName={cx('inputText')}
                        style={InputStyle.Simple}
                        size={InputSize.Small}
                        placeholder={'Scopes'}
                        initialValue={credentials.scopes}
                        onChange={this._onChangeScopes}
                    />

                    <Input
                        className={cx('inputTextWrapper')}
                        inputClassName={cx('inputText')}
                        style={InputStyle.Simple}
                        size={InputSize.Small}
                        placeholder={'Custom Domain'}
                        initialValue={credentials.customDomain}
                        onChange={this._onChangeCustomDomain}
                    />

                    <Input
                        className={cx('inputTextWrapper')}
                        inputClassName={cx('inputText')}
                        style={InputStyle.Simple}
                        size={InputSize.Small}
                        placeholder={'API Key'}
                        initialValue={credentials.apiKey}
                        onChange={this._onChangeApiKey}
                    />

                    <Row className={cx('btnCreateWrapper')}>
                        <WSPButton
                            className={cx('btnCreate')}
                            caption={buttonTitle}
                            style={ButtonStyle.Simple}
                            size={ButtonSize.Small}
                            onClick={this._buttonCreateCredentials}
                            isDisabled={isDisabled}
                        />
                    </Row>
                </Column>

                <WSPButton
                    className={cx('btnClose')}
                    icon={MDIcons.common.Close}
                    style={ButtonStyle.SimpleSecondary}
                    onClick={this._buttonCloseCreateCredentials}
                />

            </Row>
        );
    }

    @action.bound
    private _initCredentials(credentials:Credentials)
    {
        if( credentials )
            this._credentials = {...credentials};
        else
            this._credentials = Credentials.empty();
    }

    @action.bound
    private _initAuthProvidersItems(credentials:Credentials)
    {
        const authProvider = credentials ? credentials.provider : OAuth2ProviderName.GOOGLE;

        const providers = [
            {
                provider: OAuth2ProviderName.GOOGLE,
                title: OAuth2ProviderName.GOOGLE
            },
            {
                provider: OAuth2ProviderName.MICROSOFT,
                title: OAuth2ProviderName.MICROSOFT
            },
            // {
            //     provider: OAuth2ProviderName.FACEBOOK,
            //     title: OAuth2ProviderName.FACEBOOK
            // },
            {
                provider: OAuth2ProviderName.FINASTRA,
                title: OAuth2ProviderName.FINASTRA
            },
            {
                provider: OAuth2ProviderName.OKTA,
                title: OAuth2ProviderName.OKTA
            },
        ];
        this._dropdownAuthProviderItems = new ListModel(providers);
        this._dropdownAuthProviderItems.selectByValueProperty(authProvider, 'provider');
    }

    @action.bound
    _onChangeName(newValue:string)
    {
        this._credentials.name = newValue;
    }

    @action.bound
    _onChangeClientId(newValue:string)
    {
        this._credentials.clientId = newValue;
    }

    @action.bound
    _onChangeClientSecret(newValue:string)
    {
        this._credentials.clientSecret = newValue;
    }

    @action.bound
    _onChangeScopes(newValue:string)
    {
        this._credentials.scopes = newValue;
    }

    @action.bound
    _onChangeCustomDomain(newValue:string)
    {
        this._credentials.customDomain = newValue;
    }

    @action.bound
    _onChangeApiKey(newValue:string)
    {
        this._credentials.apiKey = newValue;
    }

    @bind
    _buttonCreateCredentials()
    {
        const {onChange} = this.props;

        onChange(this._credentials);
    }

    @bind
    _buttonCloseCreateCredentials()
    {
        const {onClose} = this.props;

        onClose();
    }

    @bind
    private _isValidName(newName:string, credentialsId:number)
    {
        // if( !this._credentials ) return false;
        if( !newName ) return false;

        // const name = this._credentials.name.trim();
        const name = newName.trim();

        return true;
    }

    @bind
    private _isValidInputs()
    {
        const {id, name, clientId, clientSecret} = this._credentials;

        return this._isValidName(name, id) && clientId && (this.props.isReadOnly || clientSecret);
    }

    @bind
    _authProviderCaptionRenderer(data:any, state:any, styles:IStyles):JSX.Element
    {
        const {title} = data;
        const {isOpened} = state;

        return (
            <div className={cx('dropdownPosition-caption', {isOpened})}>

                <span className={cx('dropdownPosition-title')}>
                    {title}
                </span>

                <MDIcon
                    className={cx('dropdownPosition-icon')}
                    name={MDIcons.common.ArrowDropdown}
                    size={MDIconSize.medium16}
                />
            </div>
        );
    }

    @bind
    _positionItemsRenderer(data:any, state:IListItemState, styles:IStyles):JSX.Element
    {
        const {title} = data;
        const {isSelected} = state;

        return (
            <Row className={cx('dropdownPosition-item', {isSelected})}>

                <MDIcon
                    className={cx('dropdownPosition-icon')}
                    name={MDIcons.common.Checkmark}
                />
                <div>
                    {title}
                </div>
            </Row>
        );
    }

    @action.bound
    private _onSelectAuthProvider(authProviderItem)
    {
        this._credentials.provider = authProviderItem.provider;
    }
}
